/* AdminPages — manage footer config + static (legal/corporate) pages + contact
 * info from a single admin screen. All persistence is via existing endpoints:
 *
 *   GET/PUT /api/settings/footer_config
 *   GET/PUT /api/settings/contact_info
 *   GET/PUT /api/legal/{page}    (about, kunye, ad_info, kvkk, privacy,
 *                                cookie, terms, publishing_principles, iletisim)
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Save, Link as LinkIcon } from "lucide-react";
import { AdminPageHeader, Field, TextArea, Select } from "./_helpers";

const STATIC_PAGES = [
  { key: "about", label: "Hakkımızda", url: "/kurumsal/hakkimizda" },
  { key: "kunye", label: "Künye", url: "/kurumsal/kunye" },
  { key: "ad_info", label: "Reklam", url: "/kurumsal/reklam" },
  { key: "iletisim", label: "İletişim (içerik)", url: "/kurumsal/iletisim" },
  { key: "publishing_principles", label: "Yayın İlkeleri", url: "/kurumsal/yayin-ilkeleri" },
  { key: "kvkk", label: "KVKK Aydınlatma Metni", url: "/kurumsal/kvkk" },
  { key: "privacy", label: "Gizlilik Politikası", url: "/kurumsal/gizlilik" },
  { key: "cookie", label: "Çerez Politikası", url: "/kurumsal/cerez" },
  { key: "terms", label: "Kullanım Şartları", url: "/kurumsal/kullanim-sartlari" },
];

const TABS = [
  { id: "footer", label: "Footer" },
  { id: "pages", label: "Statik Sayfalar" },
  { id: "contact", label: "İletişim Bilgileri" },
];

const DEFAULT_FOOTER = {
  tagline: "SİNE81 — Türkiye'nin 81 ilinden gerçeği yansıtan haber ve TV platformu.",
  copyright: "© {year} SİNE81 — Sine81.com — Tüm hakları saklıdır.",
  columns: [
    {
      title: "Kurumsal",
      links: [
        { label: "Hakkımızda", url: "/kurumsal/hakkimizda" },
        { label: "Künye", url: "/kurumsal/kunye" },
        { label: "Reklam", url: "/kurumsal/reklam" },
        { label: "İletişim", url: "/kurumsal/iletisim" },
        { label: "Yayın İlkeleri", url: "/kurumsal/yayin-ilkeleri" },
      ],
    },
    {
      title: "Yasal",
      links: [
        { label: "KVKK Aydınlatma Metni", url: "/kurumsal/kvkk" },
        { label: "Gizlilik Politikası", url: "/kurumsal/gizlilik" },
        { label: "Çerez Politikası", url: "/kurumsal/cerez" },
        { label: "Kullanım Şartları", url: "/kurumsal/kullanim-sartlari" },
      ],
    },
    {
      title: "Platform",
      links: [
        { label: "Anasayfa", url: "/" },
        { label: "Köşe Yazıları", url: "/kose-yazilari" },
        { label: "Video", url: "/video" },
        { label: "TV", url: "/tv" },
        { label: "Haber Hattı", url: "/haber-hatti" },
      ],
    },
  ],
};

export default function AdminPages() {
  const [tab, setTab] = useState("footer");

  return (
    <div data-testid="admin-pages">
      <AdminPageHeader
        title="Sayfa ve Footer Yönetimi"
        subtitle="Footer alanları, kurumsal sayfa içerikleri ve iletişim bilgileri buradan düzenlenir."
      />

      <div className="flex gap-2 border-b border-gray-200 mb-4">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`admin-pages-tab-${t.id}`}
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-[2px] ${
              tab === t.id
                ? "border-[#e11d2a] text-[#e11d2a]"
                : "border-transparent text-gray-600 hover:text-gray-900"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "footer" && <FooterEditor />}
      {tab === "pages" && <StaticPagesEditor />}
      {tab === "contact" && <ContactInfoEditor />}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Footer editor                                                              */
/* -------------------------------------------------------------------------- */

function FooterEditor() {
  const [cfg, setCfg] = useState(DEFAULT_FOOTER);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get("/settings/footer_config")
      .then((r) => {
        const v = r.data?.value;
        if (v && typeof v === "object") setCfg(normalizeFooter(v));
      })
      .catch(() => {});
  }, []);

  const updateColumn = (i, patch) =>
    setCfg((c) => {
      const cols = [...c.columns];
      cols[i] = { ...cols[i], ...patch };
      return { ...c, columns: cols };
    });

  const addColumn = () =>
    setCfg((c) => ({
      ...c,
      columns: [...c.columns, { title: "Yeni Kolon", links: [] }],
    }));

  const removeColumn = (i) =>
    setCfg((c) => ({ ...c, columns: c.columns.filter((_, idx) => idx !== i) }));

  const addLink = (ci) =>
    updateColumn(ci, {
      links: [...cfg.columns[ci].links, { label: "Yeni Bağlantı", url: "/" }],
    });

  const updateLink = (ci, li, patch) =>
    updateColumn(ci, {
      links: cfg.columns[ci].links.map((l, idx) => (idx === li ? { ...l, ...patch } : l)),
    });

  const removeLink = (ci, li) =>
    updateColumn(ci, { links: cfg.columns[ci].links.filter((_, idx) => idx !== li) });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings/footer_config", cfg);
      toast.success("Footer kaydedildi");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  const resetDefaults = () => {
    if (window.confirm("Varsayılan değerlere dönülecek. Mevcut düzen kaybolacak. Emin misiniz?")) {
      setCfg(DEFAULT_FOOTER);
      toast.message("Varsayılan değerler yüklendi. Kaydetmeyi unutmayın.");
    }
  };

  return (
    <div className="bg-white border border-gray-100 rounded-lg p-5 space-y-5">
      <Field
        label="Açıklama metni (logo altında)"
        value={cfg.tagline}
        onChange={(e) => setCfg({ ...cfg, tagline: e.target.value })}
        data-testid="footer-tagline"
      />
      <Field
        label="Telif metni (en altta) — {year} dinamik olarak değişir"
        value={cfg.copyright}
        onChange={(e) => setCfg({ ...cfg, copyright: e.target.value })}
        data-testid="footer-copyright"
      />

      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="font-bold text-sm">Bağlantı Kolonları</div>
          <button
            onClick={addColumn}
            className="text-xs px-2 py-1 border border-gray-200 rounded hover:bg-gray-50 flex items-center gap-1"
            data-testid="footer-add-column"
          >
            <Plus className="w-3 h-3" /> Kolon Ekle
          </button>
        </div>

        <div className="grid md:grid-cols-3 gap-3">
          {cfg.columns.map((col, ci) => (
            <div key={ci} className="border border-gray-200 rounded-md p-3">
              <div className="flex items-center justify-between mb-2">
                <input
                  value={col.title}
                  onChange={(e) => updateColumn(ci, { title: e.target.value })}
                  className="font-bold text-sm px-2 py-1 border-b border-transparent hover:border-gray-200 focus:border-[#e11d2a] focus:outline-none w-full"
                  placeholder="Kolon Başlığı"
                />
                <button
                  onClick={() => removeColumn(ci)}
                  className="text-red-500 p-1 hover:bg-red-50 rounded"
                  title="Kolonu sil"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="space-y-1.5">
                {col.links.map((l, li) => (
                  <div key={li} className="flex items-center gap-1.5">
                    <input
                      value={l.label}
                      onChange={(e) => updateLink(ci, li, { label: e.target.value })}
                      placeholder="Etiket"
                      className="flex-1 text-xs px-2 py-1 border border-gray-200 rounded"
                    />
                    <input
                      value={l.url}
                      onChange={(e) => updateLink(ci, li, { url: e.target.value })}
                      placeholder="/url"
                      className="flex-1 text-xs px-2 py-1 border border-gray-200 rounded"
                    />
                    <button
                      onClick={() => removeLink(ci, li)}
                      className="text-red-500 p-1 hover:bg-red-50 rounded shrink-0"
                      title="Bağlantıyı sil"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                <button
                  onClick={() => addLink(ci)}
                  className="w-full text-xs py-1 border border-dashed border-gray-300 hover:border-[#e11d2a] hover:text-[#e11d2a] rounded flex items-center justify-center gap-1"
                >
                  <Plus className="w-3 h-3" /> Bağlantı Ekle
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-between pt-3 border-t border-gray-100">
        <button
          onClick={resetDefaults}
          className="px-3 py-2 text-sm border border-gray-200 rounded-md hover:bg-gray-50"
        >
          Varsayılana Dön
        </button>
        <button
          onClick={save}
          disabled={saving}
          data-testid="footer-save"
          className="px-5 py-2 text-sm bg-[#e11d2a] text-white rounded-md font-bold flex items-center gap-1 disabled:opacity-50 hover:bg-[#c01825]"
        >
          <Save className="w-4 h-4" /> {saving ? "Kaydediliyor..." : "Kaydet"}
        </button>
      </div>
    </div>
  );
}

function normalizeFooter(v) {
  return {
    tagline: v.tagline || DEFAULT_FOOTER.tagline,
    copyright: v.copyright || DEFAULT_FOOTER.copyright,
    columns: Array.isArray(v.columns) && v.columns.length ? v.columns : DEFAULT_FOOTER.columns,
  };
}

/* -------------------------------------------------------------------------- */
/* Static / Legal pages editor                                                */
/* -------------------------------------------------------------------------- */

function StaticPagesEditor() {
  const [pageKey, setPageKey] = useState(STATIC_PAGES[0].key);
  const [data, setData] = useState({ title: "", content: "" });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async (key) => {
    setLoading(true);
    try {
      const r = await api.get(`/legal/${key}`);
      setData({
        title: r.data?.title || STATIC_PAGES.find((p) => p.key === key)?.label || "",
        content: r.data?.content || "",
        updated_at: r.data?.updated_at,
      });
    } catch {
      toast.error("Yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(pageKey);
  }, [pageKey]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/legal/${pageKey}`, {
        title: data.title,
        content: data.content,
        updated_at: new Date().toISOString(),
      });
      toast.success("Sayfa kaydedildi");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  const current = STATIC_PAGES.find((p) => p.key === pageKey);

  return (
    <div className="bg-white border border-gray-100 rounded-lg p-5 space-y-4">
      <div className="grid md:grid-cols-2 gap-3">
        <Select
          label="Sayfa"
          value={pageKey}
          options={STATIC_PAGES.map((p) => ({ value: p.key, label: p.label }))}
          onChange={(e) => setPageKey(e.target.value)}
          data-testid="static-page-select"
        />
        <div className="flex items-end">
          {current && (
            <a
              href={current.url}
              target="_blank"
              rel="noreferrer"
              className="text-sm text-[#e11d2a] hover:underline flex items-center gap-1"
            >
              <LinkIcon className="w-3.5 h-3.5" /> Sayfayı görüntüle: {current.url}
            </a>
          )}
        </div>
      </div>

      <Field
        label="Sayfa Başlığı"
        value={data.title}
        onChange={(e) => setData({ ...data, title: e.target.value })}
        data-testid="static-page-title"
      />
      <TextArea
        rows={16}
        label="İçerik (düz metin / satır sonlarını koruyarak yayımlanır)"
        value={data.content}
        onChange={(e) => setData({ ...data, content: e.target.value })}
        placeholder="Sayfa içeriğini buraya yazın..."
        data-testid="static-page-content"
      />

      <div className="flex justify-between items-center pt-3 border-t border-gray-100">
        <div className="text-xs text-gray-500">
          {loading ? "Yükleniyor..." : data.updated_at ? `Son güncelleme: ${new Date(data.updated_at).toLocaleString("tr-TR")}` : "Henüz kaydedilmedi"}
        </div>
        <button
          onClick={save}
          disabled={saving || loading}
          data-testid="static-page-save"
          className="px-5 py-2 text-sm bg-[#e11d2a] text-white rounded-md font-bold flex items-center gap-1 disabled:opacity-50 hover:bg-[#c01825]"
        >
          <Save className="w-4 h-4" /> {saving ? "Kaydediliyor..." : "Kaydet"}
        </button>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Contact info editor (used by Footer + Corporate iletisim page)             */
/* -------------------------------------------------------------------------- */

const DEFAULT_CONTACT = {
  company_name: "SİNE81",
  email: "info@sine81.com",
  phone: "+90 212 000 00 00",
  address: "İstanbul, Türkiye",
  map_embed_url: "",
  working_hours: "Pazartesi - Cuma 09:00 - 18:00",
};

function ContactInfoEditor() {
  const [c, setC] = useState(DEFAULT_CONTACT);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get("/settings/contact_info")
      .then((r) => {
        const v = r.data?.value;
        if (v && typeof v === "object") setC({ ...DEFAULT_CONTACT, ...v });
      })
      .catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings/contact_info", c);
      toast.success("İletişim bilgileri kaydedildi");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-gray-100 rounded-lg p-5 space-y-3">
      <div className="grid md:grid-cols-2 gap-3">
        <Field label="Kurum Adı" value={c.company_name} onChange={(e) => setC({ ...c, company_name: e.target.value })} />
        <Field label="E-posta" value={c.email} onChange={(e) => setC({ ...c, email: e.target.value })} type="email" />
        <Field label="Telefon" value={c.phone} onChange={(e) => setC({ ...c, phone: e.target.value })} />
        <Field label="Çalışma Saatleri" value={c.working_hours} onChange={(e) => setC({ ...c, working_hours: e.target.value })} />
      </div>
      <TextArea
        rows={2}
        label="Adres"
        value={c.address}
        onChange={(e) => setC({ ...c, address: e.target.value })}
      />
      <TextArea
        rows={3}
        label="Google Maps iframe URL (isteğe bağlı, src kısmı)"
        value={c.map_embed_url}
        onChange={(e) => setC({ ...c, map_embed_url: e.target.value })}
        placeholder="https://www.google.com/maps/embed?pb=..."
      />
      <div className="flex justify-end pt-3 border-t border-gray-100">
        <button
          onClick={save}
          disabled={saving}
          data-testid="contact-save"
          className="px-5 py-2 text-sm bg-[#e11d2a] text-white rounded-md font-bold flex items-center gap-1 disabled:opacity-50 hover:bg-[#c01825]"
        >
          <Save className="w-4 h-4" /> {saving ? "Kaydediliyor..." : "Kaydet"}
        </button>
      </div>
    </div>
  );
}
